import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
	Confirm,
	Input,
	Modal,
	SpinnerOverlay,
	SubmissionButtons,
	TextArea,
} from '@components/common';

import { useMutateAddPolygon } from '@hooks/polygons/useMutateAddPolygon';
import { useMutateCheckPolygonName } from '@hooks/polygons/useMutateCheckPolygonName';
import { useMutateUpdatePolygon } from '@hooks/polygons/useMutateUpdatePolygon';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setActivePolygon,
	setRightSideControls,
	setShowingCustomPolygonIds,
} from '@slices/map';

import { trim } from '@utils/common';

import { ICustomPolygonDTO } from '@interfaces/map/dto/polygonDTO';
import { TCustomPolygon } from '@interfaces/map/polygon';

import { EControlsType, EPolygonTypeDTO } from '@enums/common';
import { EPolygonType } from '@enums/map';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480, modalCloseConfirm } from '@constants/modals';
import { DEFAULT_COUNT_DIGITS } from './constants';

import { getDefaultName } from './helpers';
import { validationSchema } from './validationSchema';

import styles from './CustomPolygonForm.module.scss';

export interface ICustomPolygonFormValues {
	name?: string;
	description?: string;
}

export type TSetCustomPolygonData = Dispatch<SetStateAction<TCustomPolygon | ICustomPolygonDTO | null>>;

interface ICustomPolygonFormProps {
	closeCustomPolygon: () => void;
	customPolygons: ICustomPolygonDTO[];
	isEdit?: boolean;
	polygonData: TCustomPolygon;
	setCustomPolygonData: TSetCustomPolygonData;
	setIsDirtyForm: Dispatch<SetStateAction<boolean>>;
	setIsEditingPolygon: Dispatch<SetStateAction<boolean>>;
}

const CustomPolygonForm = ({
	closeCustomPolygon,
	customPolygons,
	isEdit,
	polygonData,
	setCustomPolygonData,
	setIsDirtyForm: setDirtyForm,
	setIsEditingPolygon,
}: ICustomPolygonFormProps) => {
	const { drawTool } = useAppSelector(map);
	const dispatch = useAppDispatch();

	const { isChecking, checkName } = useMutateCheckPolygonName();
	const [isConfirmModal, setIsConfirmModal] = useState(false);

	const isCircle = drawTool === EPolygonType.circle;

	const polygonLength = customPolygons.filter(item => item?.points).length;
	const circleLength = customPolygons.length - polygonLength;
	const polygonType = isCircle ? 'Circle' : 'Polygon';
	const length = isCircle ? circleLength : polygonLength;

	const defaultName = getDefaultName(length, polygonType);

	const existingName = customPolygons.find(item => item.name === defaultName)?.name;
	const validPolygonName = existingName
		? getDefaultName(+(existingName.slice(existingName.length - DEFAULT_COUNT_DIGITS)), polygonType)
		: defaultName;

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setError,
		setValue,
	} = useForm<ICustomPolygonFormValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(validationSchema),
		defaultValues: {
			name: (polygonData as ICustomPolygonDTO)?.name || validPolygonName as string,
			description: (polygonData as ICustomPolygonDTO)?.description || '',
		},
	});

	useEffect(() => {
		setDirtyForm(isDirty);
	}, [isDirty, setDirtyForm]);

	useEffect(() => {
		if (!isEdit) setValue('name', `${validPolygonName}`, { shouldDirty: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onSuccessCreation = (data: ICustomPolygonDTO) => {
		closeCustomPolygon();

		if (data?.id) {
			dispatch(setShowingCustomPolygonIds([data.id]));
			dispatch(setActivePolygon({
				id: data.id,
				isCustom: true,
			}));
		}

		if (!isEdit) dispatch(setRightSideControls(EControlsType.customPolygonList));

		setIsEditingPolygon(false);
		setCustomPolygonData(null);
	};

	const { createCustomPolygon } = useMutateAddPolygon(onSuccessCreation);
	const { updatePolygon } = useMutateUpdatePolygon(onSuccessCreation);

	const handleCloseForm = () => {
		if (isDirty) setIsConfirmModal(true);
		else closeCustomPolygon();
	};

	const handleCloseConfirmModal = () => setIsConfirmModal(false);

	const onSubmit = async (values: ICustomPolygonFormValues) => {
		try {
			const description = values.description
				? { description: values.description }
				: {};

			if (isDirty && values.name !== (polygonData as ICustomPolygonDTO)?.name && values.name) {
				await checkName(trim(values.name));
			}

			const payload: ICustomPolygonDTO = {
				...polygonData,
				...description,
				name: trim(values.name) as string,
				polygonType: (polygonData as any)?.radius
					? EPolygonTypeDTO.circle
					: EPolygonTypeDTO.polygon,
			};

			if (isEdit && payload?.id) {
				await updatePolygon({
					id: payload.id,
					data: payload,
				} as any);
			} else {
				await createCustomPolygon(payload as any);
			}
		} catch (e: any) {
			setError('name', {
				type: 'custom',
				message: e?.errors[0].message,
			});
		}
	};

	return (
		<div className={styles.container}>
			<Input
				errorMessage={errors?.name?.message}
				label='Polygon Name'
				name='name'
				placeholder='Enter polygon name'
				register={register}
				required
				withUseForms
			/>
			<TextArea
				errorMessage={errors?.description?.message}
				label='Description'
				name='description'
				placeholder='Enter polygon description or important comments'
				register={register}
				rows={5}
				withUseForms
			/>
			<SubmissionButtons
				isLoading={isChecking}
				negativeHandler={handleCloseForm}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel={isEdit ? 'Save' : 'Add'}
			/>
			{isChecking && <SpinnerOverlay />}
			<Modal
				isOpen={isConfirmModal}
				onClose={handleCloseConfirmModal}
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={handleCloseConfirmModal}
					onSubmit={closeCustomPolygon}
					question={modalCloseConfirm.question}
				>
					{modalCloseConfirm.children}
				</Confirm>
			</Modal>
		</div>
	);
};

export default CustomPolygonForm;
